import React from "react";
import { Link } from "react-router-dom";
import {
  Building2, Hammer, Wrench, PlusSquare, Maximize2, Shield,
  CheckCircle2, Clock, MessageSquare, Handshake, ArrowRight, ArrowUpRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  heroContent, stats, services, whyChooseUs, images
} from "../data/data";

const iconMap = {
  Building2, Hammer, Wrench, PlusSquare, Maximize2, Shield,
  CheckCircle2, Clock, MessageSquare, Handshake
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-[92vh] flex items-center bg-pc-primary overflow-hidden">
    {/* Background image with overlay */}
    <div className="absolute inset-0">
      <img
        src={images.hero}
        alt=""
        className="w-full h-full object-cover opacity-15"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-pc-primary via-pc-primary/95 to-pc-primary/80" />
    </div>

    {/* Decorative elements */}
    <div className="absolute top-0 right-0 w-1/3 h-full bg-pc-accent/5 -skew-x-12 translate-x-20 hidden lg:block" />
    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
      <div className="max-w-2xl">
        {/* Badge */}
        <div className="inline-flex items-center gap-3 mb-8">
          <span className="w-10 h-[2px] bg-pc-accent" />
          <span className="text-white/60 font-body text-sm tracking-wide">
            Established 1995 &middot; Gujarat, India
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-heading text-4xl md:text-5xl lg:text-[60px] font-extrabold text-white leading-[1.08] tracking-tight mb-6">
          Building Trust
          <br />
          <span className="text-pc-accent">Through Quality</span>
          <br />
          Construction
        </h1>

        <p className="font-body text-lg text-white/55 leading-relaxed mb-10 max-w-lg">
          {heroContent.subheadline}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/contact">
            <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-13 rounded-lg w-full sm:w-auto transition-colors duration-200">
              Get a Quote
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/projects">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 hover:text-white text-sm font-heading font-medium px-8 h-13 rounded-lg w-full sm:w-auto transition-colors duration-200"
            >
              View Our Projects
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats floating card - desktop only */}
      <div className="hidden lg:block absolute bottom-16 right-8 xl:right-16">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <div className="grid grid-cols-2 gap-6">
            {stats.slice(0, 4).map((stat, i) => (
              <div key={i} className="text-center px-4">
                <p className="font-heading text-3xl font-extrabold text-white mb-1">
                  {stat.value}
                </p>
                <p className="font-body text-xs text-white/40 uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Mobile Stats
const MobileStats = () => (
  <section className="lg:hidden bg-white border-b border-pc-border py-10">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid grid-cols-2 gap-6">
        {stats.map((stat, i) => (
          <div key={i} className="text-center">
            <p className="font-heading text-3xl font-extrabold text-pc-primary">{stat.value}</p>
            <p className="font-body text-xs text-pc-muted uppercase tracking-wider mt-1">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Services Overview
const ServicesOverview = () => (
  <section className="bg-pc-light py-24 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
        <div>
          <div className="accent-bar mb-5" />
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-pc-primary tracking-tight mb-3">
            What We Do
          </h2>
          <p className="font-body text-pc-muted text-base leading-relaxed max-w-lg">
            From new construction to structural strengthening, we handle all aspects of
            civil construction with care and precision.
          </p>
        </div>
        <Link to="/services">
          <Button
            variant="outline"
            className="border-pc-primary/20 text-pc-primary hover:bg-pc-primary hover:text-white text-sm font-heading font-semibold px-6 h-11 rounded-lg transition-colors duration-200"
          >
            All Services
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.filter(s => s.id !== 7).map((service) => {
          const Icon = iconMap[service.icon];
          return (
            <Card
              key={service.id}
              className="bg-white border-pc-border/60 hover:shadow-xl card-accent-hover group transition-shadow duration-300 rounded-xl"
            >
              <CardContent className="p-7">
                <div className="w-12 h-12 rounded-lg bg-pc-accent/8 flex items-center justify-center mb-5 group-hover:bg-pc-accent/15 transition-colors duration-300">
                  {Icon && <Icon className="w-5 h-5 text-pc-accent" />}
                </div>
                <h3 className="font-heading text-lg font-bold text-pc-primary mb-2">
                  {service.title}
                </h3>
                <p className="font-body text-sm text-pc-muted leading-relaxed">
                  {service.shortDescription}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

// Why Choose Us
const WhyChooseUs = () => (
  <section className="bg-white py-24 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left content */}
        <div className="lg:col-span-5">
          <div className="accent-bar mb-5" />
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-pc-primary tracking-tight mb-4">
            Why Clients <br className="hidden md:block" />Trust Us
          </h2>
          <p className="font-body text-pc-muted text-base leading-relaxed mb-8">
            Our reputation is built on consistent delivery, honest communication,
            and workmanship that stands the test of time.
          </p>
          <Link to="/about">
            <Button
              variant="outline"
              className="border-pc-accent text-pc-accent hover:bg-pc-accent hover:text-white text-sm font-heading font-semibold px-6 h-11 rounded-lg transition-colors duration-200"
            >
              About Our Company
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        {/* Right cards */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {whyChooseUs.map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <div
                key={index}
                className="p-6 rounded-xl border border-pc-border/60 hover:border-pc-accent/20 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-lg bg-pc-primary/5 flex items-center justify-center mb-4 group-hover:bg-pc-accent/10 transition-colors duration-300">
                  {Icon && <Icon className="w-5 h-5 text-pc-primary group-hover:text-pc-accent transition-colors duration-300" />}
                </div>
                <h3 className="font-heading text-base font-bold text-pc-primary mb-2">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-pc-muted leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CtaSection = () => (
  <section className="relative bg-pc-primary py-24 texture-grain overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pc-accent/30 to-transparent" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(207,94,41,0.08),transparent_60%)]" />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-4">
          Have a Construction Project in Mind?
        </h2>
        <p className="font-body text-white/50 text-lg mb-10 leading-relaxed">
          Let us discuss your requirements. We offer straightforward estimates
          and honest guidance — no pressure, no obligations.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact">
            <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-12 rounded-lg transition-colors duration-200">
              Contact Us Today
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/services">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 hover:text-white text-sm font-heading font-medium px-8 h-12 rounded-lg transition-colors duration-200"
            >
              Explore Services
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <HeroSection />
      <MobileStats />
      <ServicesOverview />
      <WhyChooseUs />
      <CtaSection />
    </main>
  );
}
